import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to build a Remark plugin to supercharge your static site",
  "description": "",
  "published": "2019-01-09T00:00:00.000Z",
  "redirect_from": ["/blog/buildremark-plugin-supercharge-static-site/swizec/8860"],
  "categories": "Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1547049367743.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Remark plugins are a little esoteric, and with the rise of Gatsby, they’re becoming more and more important.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/remarkjs/remark"
      }}>{`Remark`}</a>{` is a markdown parser built in JavaScript. "Parser" doesn't do Remark justice. Remark is a whole compiler ecosystem built on plugins.`}</p>
    <p>{`It's the backbone of all Gatsby magic that involves converting markdown into HTML.`}</p>
    <p>{`There are `}<a parentName="p" {...{
        "href": "https://github.com/remarkjs/remark/blob/master/doc/plugins.md"
      }}>{`many Remark plugins`}</a>{`, and the list keeps growing. This article is about how to build your own. ✌️`}</p>
    <h2 {...{
      "id": "why-you-should-build-a-remark-plugin"
    }}>{`Why you should build a Remark plugin`}</h2>
    <lite-youtube {...{
      "videoid": "LWvaOGVHLfE",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Because you want a feature you can't find yet. Simple as that.`}</p>
    <p>{`For me, it was two features, so I built 2 plugins. But I packaged them both in the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/markdown-to-tweet"
      }}>{`markdown-to-tweet`}</a>{` package.`}</p>
    <p>{`Here's an early preview 👇`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Look at me, I can 𝘁𝘄𝗲𝗲𝘁 𝗶𝗻 𝗯𝗼𝗹𝗱 and even use 𝘦𝘮𝘱𝘩𝘢𝘴𝘪𝘴 wherever I want. Even 𝘭𝘪𝘬𝘦 𝘵𝘩𝘪𝘴`}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/ThreadCompiler?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#ThreadCompiler`}</a>{` step 1 🤘 `}<a parentName="p" {...{
          "href": "https://t.co/Zp7AIzFBTA"
        }}>{`pic.twitter.com/Zp7AIzFBTA`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1082086702926024705"
      }}>{`January 7, 2019`}</a></blockquote>
    <p>{`My `}<inlineCode parentName="p">{`markdown-to-tweet`}</inlineCode>{` does 4 things:`}</p>
    <ol>
      <li parentName="ol">{`It converts `}<inlineCode parentName="li">{`**bold**`}</inlineCode>{` text into utf8 bolds: 𝗯𝗼𝗹𝗱`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`_italic_`}</inlineCode>{` text into utf8 italics: 𝘪𝘵𝘢𝘭𝘪𝘤`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`\\`}</inlineCode>{`code`}{`\``}{`\``}{` text into monospace: 𝚌𝚘𝚍𝚎`}</li>
      <li parentName="ol">{`Code blocks into carbon.now.sh screenshots`}</li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "524px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/d74bc0282764cd02c1aaf51a8615d0cd/664c8/techletter.app-screenshot-1547049367743.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "59.64125560538116%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'239\\'%20viewBox=\\'0%200%20400%20239\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M25%2039v162c2%203%20348%203%20350%200l1-81-1-81c-2-3-348-3-350%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/d74bc0282764cd02c1aaf51a8615d0cd/ca0a1/techletter.app-screenshot-1547049367743.webp 223w", "/static/d74bc0282764cd02c1aaf51a8615d0cd/75680/techletter.app-screenshot-1547049367743.webp 445w", "/static/d74bc0282764cd02c1aaf51a8615d0cd/1c5aa/techletter.app-screenshot-1547049367743.webp 524w"],
              "sizes": "(max-width: 524px) 100vw, 524px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/d74bc0282764cd02c1aaf51a8615d0cd/e92b6/techletter.app-screenshot-1547049367743.png 223w", "/static/d74bc0282764cd02c1aaf51a8615d0cd/e66bf/techletter.app-screenshot-1547049367743.png 445w", "/static/d74bc0282764cd02c1aaf51a8615d0cd/664c8/techletter.app-screenshot-1547049367743.png 524w"],
              "sizes": "(max-width: 524px) 100vw, 524px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/d74bc0282764cd02c1aaf51a8615d0cd/664c8/techletter.app-screenshot-1547049367743.png",
              "alt": "techletter app screenshot 1547049367743",
              "title": "techletter app screenshot 1547049367743",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Screenshots work through an AWS lambda I built for `}<a parentName="p" {...{
        "href": "https://techletter.app"
      }}>{`techletter.app`}</a>{` a few months ago and never got around to writing a blog about. I should fix that 🤔`}</p>
    <p>{`Anyway, I couldn't find a remark plugin that does that, so I built my own. That was my excuse. `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=NmXw8yMTjys"
      }}>{`Kent C. Dodds`}</a>{` built one to turn YouTube and Twitter links into embeds on his blog.`}</p>
    <p>{`You can try my `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/markdown-to-tweet"
      }}>{`markdown-to-tweet`}</a>{` thingy in this rudimentary Codesandbox 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/n375856710",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Maybe I should release the underlying Remark plugins separately 🤔`}</p>
    <h2 {...{
      "id": "how-to-build-a-simple-remark-plugin"
    }}>{`How to build a simple Remark plugin`}</h2>
    <lite-youtube {...{
      "videoid": "X7mZAsfwYng",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Like I said, you can think of Remark as a compiler ecosystem. If you look at the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Compiler"
      }}>{`traditional compiler architecture`}</a>{` from Wikipedia…`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/d62042a9997ee67b8331c643261bbeb3/29114/Compiler-architecturea50h8f.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "113.00448430493275%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'452\\'%20viewBox=\\'0%200%20400%20452\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M6%2030v29h53V0H6v30m208-1l1%2030h53V1l-27-1h-27v29M9%2030v26h47V3H9v27m208-1l1%2027h6l23-1h17V3h-47v26m172-1l-3%202h-2c0-3-5%200-5%202%200%203%202%205%204%203h13v-1h-3l2-1c2%200%202-1%202-2-1-2-4-3-5-1-1%201-1%201-1-1l-1-2-1%201m-84%202v2c-2%201%200%204%202%204h3c2%203%204%202%205%200%201-1%202-2%203-1h3v-1h-3l2-1c2%200%202-1%202-2-1-2-5-3-5-1h-12m-64%2045c0%2013%200%2014-2%2014-3%200-3%201%200%206l3%204%202-4c4-6%204-6%201-6-2%200-2-1-2-14l-1-15-1%2015M29%2075c0%2013%200%2014-2%2014l-2%201c0%201%206%2011%206%209l2-5c3-4%203-5%200-5-2%200-2-1-2-14l-1-14-1%2014M0%20113v51l1%2049h148V113l-74-1-75%201m213%2049v50h149v-99l-75-1h-74v50M3%20162v48h143v-95h-5c-4%200-5%200-4%202v7c-1%202-1%201-1-4v-5H93c-1%200-3%205-2%208l-1%202c-2-1-1-6%201-9v-2l-15%201H63v5c0%204-2%206-4%202l-2-1%201%202c1%201%200%201-2%201s-3-1-3-3l-1-2-1%202c0%202-1%203-2%203l-2-1h-5l1-3c1-5%202-5%204%200%202%203%202%203%202%201l1-3%202-1c1%200%202%200%203%202%200%201%201%202%203%201v-1c-2%200-2%200-1-1%201-2%204%200%204%203%200%202%201%201%201-2l1-4H41c-2%200-2%201-2%204l-1%205v-5l1-4h-8c-6%200-8%200-7%201h-1l-10-1-10-1v48m213%200v48h143v-96H216v48m23-2c0%202%200%204-1%202%200-3-3-5-4-3-1%201-1%201%201%201v1l-4-1c-2-3-5-1-5%202%200%202%200%202-2-1-2-5-3-5-5-1v4h5l2%201%202-2%201-3%201%202c0%202%201%203%203%203s3%200%202-1l-1-2%202%201c2%203%204%202%204-1v-2l1%202c1%202%202%203%201%204v1l4-8-2%201c0%202%200%202-2%200l-2-3c-1-1-1%201-1%203m133%2057l-3%202h-2c0-3-5%200-5%202%200%203%202%205%204%203h13c1%200%201-1-1-1-2-1-2-1%200-1%203%200%202-4-1-3-2%200-3-1-3-2l-1-1-1%201m-273%209l6%2013c7%2011%207%2012%205%2013-1%202-1%202%201%203l5%203c2%201%202%201%202-4%200-6%200-6-2-5s-3%200-9-12c-7-12-8-13-8-11m160%2011c-6%2012-7%2013-9%2012s-2-1-2%205%200%206%207%201c2-1%202-1%200-3a17646%2017646%200%20004-15m-154%2039v10h154v-19l-77-1h-77v10m2%200v8h149v-16H107v8m87-4c0%201-5%203-6%202-2-2-4%200-4%203%200%202%202%204%204%202h13v-1h-3l2-1c2%200%203-3%200-4l-3%201c-1%201-1%201-1-1l-1-2-1%201m-81%2036c-9%2016-11%2019-12%2018-2-2-3-1-3%205v5l4-2c5-3%206-4%204-5s-1-3%205-13l7-13c0-3-2%200-5%205m136-5l6%2012c6%2011%207%2013%205%2014-1%202-1%202%203%204l5%203v-6c0-5%200-5-2-4s-3%200-9-12c-7-12-8-13-8-11M51%20355v16h92v-32H51v16m183%200v16h92v-32h-92v16m-181%200v14h87v-14l1-14H53v14m183%200v14h88v-28h-88v14M73%20413v23l16-1h17v-22H73m183%2011v12h33v-23h-33v11\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d62042a9997ee67b8331c643261bbeb3/ca0a1/Compiler-architecturea50h8f.webp 223w", "/static/d62042a9997ee67b8331c643261bbeb3/75680/Compiler-architecturea50h8f.webp 445w", "/static/d62042a9997ee67b8331c643261bbeb3/8d1ba/Compiler-architecturea50h8f.webp 890w", "/static/d62042a9997ee67b8331c643261bbeb3/3838e/Compiler-architecturea50h8f.webp 1335w", "/static/d62042a9997ee67b8331c643261bbeb3/e11e5/Compiler-architecturea50h8f.webp 1780w", "/static/d62042a9997ee67b8331c643261bbeb3/882b9/Compiler-architecturea50h8f.webp 1920w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d62042a9997ee67b8331c643261bbeb3/e92b6/Compiler-architecturea50h8f.png 223w", "/static/d62042a9997ee67b8331c643261bbeb3/e66bf/Compiler-architecturea50h8f.png 445w", "/static/d62042a9997ee67b8331c643261bbeb3/4ef49/Compiler-architecturea50h8f.png 890w", "/static/d62042a9997ee67b8331c643261bbeb3/4e814/Compiler-architecturea50h8f.png 1335w", "/static/d62042a9997ee67b8331c643261bbeb3/701e9/Compiler-architecturea50h8f.png 1780w", "/static/d62042a9997ee67b8331c643261bbeb3/29114/Compiler-architecturea50h8f.png 1920w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d62042a9997ee67b8331c643261bbeb3/4ef49/Compiler-architecturea50h8f.png",
                "alt": "Compiler architecture",
                "title": "Compiler architecture",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Compiler architecture`}</figcaption>{`
  `}</figure></p>
    <p>{`…your plugin sits squarely in that `}<em parentName="p">{`"middle code generation"`}</em>{` part.`}</p>
    <p>{`A basic Remark call goes something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import remark from "remark";
import html from "remark-html";

// ...

remark()
  .use(html)
  .process(input_markdown, function (err, html_output) {
    if (err) throw err;

    console.log(html_output);
  });
`}</code></pre>
    <p>{`You import `}<inlineCode parentName="p">{`remark`}</inlineCode>{` and `}<inlineCode parentName="p">{`remark-html`}</inlineCode>{`, instantiate remark, and tell it to use the `}<inlineCode parentName="p">{`html`}</inlineCode>{` plugin. Then you call `}<inlineCode parentName="p">{`.process`}</inlineCode>{` on your input source, and out comes a parsed HTML for you to use.`}</p>
    <p>{`Remark itself doesn't do much. It uses `}<a parentName="p" {...{
        "href": "https://github.com/unifiedjs/unified"
      }}>{`unified`}</a>{` under the hood to parse Markdown into a MDAST – markdown abstract syntax tree.`}</p>
    <p>{`Plugins take this syntax tree and perform transformations. The html plugin, for example, turns nodes into HTML.`}</p>
    <h3 {...{
      "id": "transforming-mdast-nodes"
    }}>{`Transforming MDAST nodes`}</h3>
    <p>{`Thinking in abstract syntax trees is hard. Lots of recursion and strange patterns if you want to do it yourself. Just think of the last time you tried manipulating the DOM directly. It's a mess.`}</p>
    <p>{`Lucky for you, the Remark/Unified ecosystem comes with helper methods. It's like having jQuery for abstract syntax trees.`}</p>
    <p>{`Say you wanted to build a plugin that transforms `}<inlineCode parentName="p">{`**bold**`}</inlineCode>{` nodes into utf8 bolds. You find a function that handles the utf8 conversion and build a Remark plugin.`}</p>
    <p>{`Your Remark plugin is a function that returns a transform. Also a function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import visit from "unist-util-visit";
import { bolden } from "./unicodeTransformer";

function utf8() {
  return function transformer(tree, file) {
    visit(tree, "strong", bold);

    function bold(node) {
      node.type = "text";
      node.value = node.children.map((child) => bolden(child.value)).join(" ");
    }
  };
}

export default utf8;
`}</code></pre>
    <p>{`Simple as that. Let me explain.`}</p>
    <p>{`You import the `}<inlineCode parentName="p">{`visit`}</inlineCode>{` helper from Unified utilities, prefixed always with `}<inlineCode parentName="p">{`unist-util`}</inlineCode>{` for some reason. There's also a `}<inlineCode parentName="p">{`map`}</inlineCode>{` and a `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` and all the other methods you commonly use to work with data.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`visit`}</inlineCode>{` method deals with the mess and recursion of navigating around an abstract syntax tree.`}</p>
    <p>{`Your `}<inlineCode parentName="p">{`transformer`}</inlineCode>{` function gets the current `}<inlineCode parentName="p">{`tree`}</inlineCode>{` and a reference to the `}<inlineCode parentName="p">{`file`}</inlineCode>{` that's being compiled.`}</p>
    <p>{`You call `}<inlineCode parentName="p">{`visit()`}</inlineCode>{` to do your manipulations.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`visit(tree, "strong", bold);
`}</code></pre>
    <p>{`This calls your `}<inlineCode parentName="p">{`bold`}</inlineCode>{` function on every node of type `}<inlineCode parentName="p">{`strong`}</inlineCode>{` in your `}<inlineCode parentName="p">{`tree`}</inlineCode>{`. You can see a `}<a parentName="p" {...{
        "href": "https://github.com/syntax-tree/mdast"
      }}>{`list of available MDAST node types here`}</a>{`.`}</p>
    <p>{`Took me a while to find that list. It helps a lot. 😅`}</p>
    <p>{`The manipulation itself happens in your node visitor – the `}<inlineCode parentName="p">{`bold`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function bold(node) {
  node.type = "text";
  node.value = node.children.map((child) => bolden(child.value)).join(" ");
}
`}</code></pre>
    <p>{`You can do whatever you want. In this case, we're changing node type to `}<inlineCode parentName="p">{`text`}</inlineCode>{` so future plugins know not to mess with it. Then we take all its children, which are `}<inlineCode parentName="p">{`text`}</inlineCode>{` nodes, transform their text, and set the resulting string as the `}<inlineCode parentName="p">{`value`}</inlineCode>{` of the current node.`}</p>
    <p>{`Text nodes with values are the lowest level in MDAST. They're strings that (usually) don't get changed further and eventually flatten into your output.`}</p>
    <p>{`Changing node type is very important if you don't want other plugins to mess with your changes. Or if you `}<em parentName="p">{`do`}</em>{` and want to tell them how.`}</p>
    <p>{`This is called `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Visitor_pattern"
      }}>{`the visitor pattern`}</a>{`, by the way. It works because JavaScript uses pass-by-reference.`}</p>
    <h2 {...{
      "id": "how-to-build-an-async-remark-plugin"
    }}>{`How to build an async Remark plugin`}</h2>
    <lite-youtube {...{
      "videoid": "uzzvwnjtaPs",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Asynchronous Remark plugins is where it gets tricky. Remark `}<em parentName="p">{`does`}</em>{` support async plugins, but you might find it has a tendency to skip your plugin if you do it wrong. Had that problem myself.`}</p>
    <p>{`Thanks to `}<a parentName="p" {...{
        "href": "https://www.huynguyen.io/2018-05-remark-gatsby-plugin-part-3/"
      }}>{`Huy Nguyen's post`}</a>{` for helping me figure it out.`}</p>
    <p>{`Async plugins work in two phases:`}</p>
    <ol>
      <li parentName="ol">{`Collect nodes to change`}</li>
      <li parentName="ol">{`Change those nodes`}</li>
    </ol>
    <p>{`Around that, your transformer has to return a Promise and resolve it after you're done changing nodes. Like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function codeScreenshot() {
  return (tree) =>
    new Promise(async (resolve, reject) => {
      const nodesToChange = [];
      visit(tree, "code", (node) => {
        nodesToChange.push({
          node,
        });
      });
      for (const { node } of nodesToChange) {
        try {
          const url = await getCodeScreenshot(node.value);
          node.value = url;
        } catch (e) {
          console.log("ERROR", e);
          return reject(e);
        }
      }

      resolve();
    });
}
`}</code></pre>
    <p><inlineCode parentName="p">{`codeScreenshot`}</inlineCode>{` is the Remark plugin in this case. It returns a function that returns a Promise.`}</p>
    <p>{`Inside that Promise, you can see the two phases of an async Remark plugin.`}</p>
    <h3 {...{
      "id": "collect-nodes-to-change"
    }}>{`Collect nodes to change`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const nodesToChange = [];
visit(tree, "code", (node) => {
  nodesToChange.push({
    node,
  });
});
`}</code></pre>
    <p>{`The first step collects nodes we want to change. We're still using the visitor pattern with the `}<inlineCode parentName="p">{`visit`}</inlineCode>{` helper.`}</p>
    <p>{`But instead of changing the nodes, we push them into a `}<inlineCode parentName="p">{`nodesToChange`}</inlineCode>{` array. That's because the `}<inlineCode parentName="p">{`visit`}</inlineCode>{` function can't deal with asynchronous code.`}</p>
    <p>{`This is where I got stuck for a couple hours 😅`}</p>
    <h3 {...{
      "id": "change-those-nodes"
    }}>{`Change those nodes`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`for (const { node } of nodesToChange) {
  try {
    const url = await getCodeScreenshot(node.value);
    node.value = url;
  } catch (e) {
    console.log("ERROR", e);
    return reject(e);
  }
}

resolve();
`}</code></pre>
    <p>{`Here we iterate over the nodes we collected earlier. `}<inlineCode parentName="p">{`getCodeScreenshot`}</inlineCode>{` takes a node value, passes it into an API, and returns a URL. Eventually.`}</p>
    <p>{`We `}<inlineCode parentName="p">{`await`}</inlineCode>{` that url, then change the node. This is where I should change the node type as well. Maybe turn it into an image? 🤔`}</p>
    <p>{`Thanks to how `}<inlineCode parentName="p">{`await`}</inlineCode>{` magic works, we know our loop finishes only once all the URls have been fetched from the internet. We call `}<inlineCode parentName="p">{`resolve()`}</inlineCode>{` to tell Remark we're done transforming nodes.`}</p>
    <p>{`Remark handles the rest.`}</p>
    <h2 {...{
      "id": "in-conclusion"
    }}>{`In conclusion`}</h2>
    <ul>
      <li parentName="ul">{`Remark plugins are functions.`}</li>
      <li parentName="ul">{`Use the visitor pattern to manipulate nodes`}</li>
      <li parentName="ul">{`For async plugins collect first, change later`}</li>
    </ul>
    <p>{`That's how you build a Remark plugin. You have a new superpower 💪`}</p>
    <p>{`Please use it to add wonderful magnificent things to the Remark and Gatsby ecosystem and supercharge all our websites.`}</p>
    <p>{`Everyone's moving their blogs to Gatsby right now. It's hot. `}<strong parentName="p">{`You`}</strong>{` can make them hotter!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      